import { graphql } from "gatsby";
import React from "react";
import { twJoin } from "tailwind-merge";

import CheckCircleSimple from "../../assets/icons/checkCircleSimple.svg";
import Minus from "../../assets/icons/minus.svg";
import RichText from "../richText";

type Props = {
  slice:
    | Queries.TreatmentComparisonSliceFragment
    | Queries.TreatmentAddonComparisonSliceFragment
    | Queries.MarketingTreatmentComparisonSliceFragment
    | Queries.ContentComparisonSliceFragment
    | Queries.LocationComparisonSliceFragment
    | Queries.DentistComparisonSliceFragment;
};

const Comparison: React.FC<Props> = ({ slice }) => {
  const { primary, items } = slice;
  const { title, text, criteria1, criteria2, bgGrey } = primary;

  return (
    <section
      className={twJoin(
        "p-t-xxxl-fluid p-b-xxxl-fluid mx-auto max-w-screen-max",
        bgGrey ? "bg-neutral-50" : "bg-white",
      )}
    >
      <div className="col-span-full gap-0 mx-5 max-w-screen-xl md:mx-11 lg:mx-24 xl:mx-auto baseGrid lg:gap-x-lg">
        <section className="grid col-span-full lg:col-start-5 p-b-sm-fluid lg:col-span-16">
          <RichText data={title?.richText} className="block mb-2 font-medium" />
          <RichText data={text?.richText} className="block typo-base text-black/60" />
        </section>
        <section className="grid grid-cols-4 col-span-full md:grid-cols-12 lg:col-start-5 lg:col-span-16">
          <RichText
            data={criteria1?.richText}
            className="flex col-start-3 p-5 font-bold text-center rounded-t-lg border border-b-0 md:col-span-2 md:col-start-9 typo-small border-primary-600"
          />
          <RichText data={criteria2?.richText} className="flex p-5 text-center md:col-span-2 typo-h6" />
          {items.map((item, index) => {
            const { criteriaTitle, value1, value2, textValue1, textValue2 } = item;

            const resolveValue1 =
              textValue1?.text?.length && textValue1?.text?.length > 1 ? (
                <RichText
                  data={textValue1.richText}
                  className={twJoin(
                    "typo-small border-x border-t border-primary-600 border-t-black/10 p-5 text-center md:col-span-2",
                    items.length - 1 === index && "rounded-b-lg border-b",
                  )}
                />
              ) : (
                <div
                  className={twJoin(
                    "typo-small border-x border-t border-primary-600 border-t-black/10 p-5 md:col-span-2",
                    items.length - 1 === index && "rounded-b-lg border-b",
                  )}
                >
                  {value1 ? (
                    <CheckCircleSimple width="24" height="24" className="block mx-auto text-black" />
                  ) : (
                    <Minus width="24" height="24" className="block mx-auto text-black" />
                  )}
                </div>
              );
            const resolveValue2 =
              textValue2?.text?.length && textValue2?.text?.length > 1 ? (
                <RichText
                  data={textValue2.richText}
                  className="p-5 text-center border-t md:col-span-2 typo-small border-t-black/10"
                />
              ) : (
                <div className="p-5 border-t md:col-span-2 typo-small border-t-black/10">
                  {value2 ? (
                    <CheckCircleSimple width="24" height="24" className="block mx-auto text-black" />
                  ) : (
                    <Minus width="24" height="24" className="block mx-auto text-black" />
                  )}
                </div>
              );

            return (
              <div key={index} className="contents group">
                <RichText
                  data={criteriaTitle.richText}
                  className="col-span-2 p-5 pl-0 border-t md:col-span-8 typo-small border-t-black/10 text-black/60"
                />
                {resolveValue1}
                {resolveValue2}
              </div>
            );
          })}
        </section>
      </div>
    </section>
  );
};

export const query = graphql`
  fragment LocationComparisonSlice on PrismicLocationDataBodyComparison {
    ...Slice

    primary {
      title: comparison_title {
        ...RichText
      }
      text: comparison_text {
        ...RichText
      }
      criteria1: comparison_first_item {
        ...RichText
      }
      criteria2: comparison_second_item {
        ...RichText
      }
      bgGrey: comparison_background_color
    }
    items {
      criteriaTitle: comparison_criteria {
        ...RichText
      }
      value1: comparison_criteria_first_value
      textValue1: comparison_criteria_first_value_text {
        ...RichText
      }
      value2: comparison_criteria_second_value
      textValue2: comparison_criteria_second_value_text {
        ...RichText
      }
    }
  }

  fragment ContentComparisonSlice on PrismicPageContentDataBodyComparison {
    ...Slice

    primary {
      title: comparison_title {
        ...RichText
      }
      text: comparison_text {
        ...RichText
      }
      criteria1: comparison_first_item {
        ...RichText
      }
      criteria2: comparison_second_item {
        ...RichText
      }
      bgGrey: comparison_background_color
    }
    items {
      criteriaTitle: comparison_criteria {
        ...RichText
      }
      value1: comparison_criteria_first_value
      textValue1: comparison_criteria_first_value_text {
        ...RichText
      }
      value2: comparison_criteria_second_value
      textValue2: comparison_criteria_second_value_text {
        ...RichText
      }
    }
  }

  fragment DentistComparisonSlice on PrismicPageDentistDataBodyComparison {
    ...Slice

    primary {
      title: comparison_title {
        ...RichText
      }
      text: comparison_text {
        ...RichText
      }
      criteria1: comparison_first_item {
        ...RichText
      }
      criteria2: comparison_second_item {
        ...RichText
      }
      bgGrey: comparison_background_color
    }
    items {
      criteriaTitle: comparison_criteria {
        ...RichText
      }
      value1: comparison_criteria_first_value
      textValue1: comparison_criteria_first_value_text {
        ...RichText
      }
      value2: comparison_criteria_second_value
      textValue2: comparison_criteria_second_value_text {
        ...RichText
      }
    }
  }

  fragment TreatmentComparisonSlice on PrismicTreatmentDataBodyComparison {
    ...Slice

    primary {
      title: comparison_title {
        ...RichText
      }
      text: comparison_text {
        ...RichText
      }
      criteria1: comparison_first_item {
        ...RichText
      }
      criteria2: comparison_second_item {
        ...RichText
      }
      bgGrey: comparison_background_color
    }
    items {
      criteriaTitle: comparison_criteria {
        ...RichText
      }
      value1: comparison_criteria_first_value
      textValue1: comparison_criteria_first_value_text {
        ...RichText
      }
      value2: comparison_criteria_second_value
      textValue2: comparison_criteria_second_value_text {
        ...RichText
      }
    }
  }

  fragment TreatmentAddonComparisonSlice on PrismicTreatmentAddonDataBodyComparison {
    ...Slice

    primary {
      title: comparison_title {
        ...RichText
      }
      text: comparison_text {
        ...RichText
      }
      criteria1: comparison_first_item {
        ...RichText
      }
      criteria2: comparison_second_item {
        ...RichText
      }
      bgGrey: comparison_background_color
    }
    items {
      criteriaTitle: comparison_criteria {
        ...RichText
      }
      value1: comparison_criteria_first_value
      textValue1: comparison_criteria_first_value_text {
        ...RichText
      }
      value2: comparison_criteria_second_value
      textValue2: comparison_criteria_second_value_text {
        ...RichText
      }
    }
  }

  fragment MarketingTreatmentComparisonSlice on PrismicMarketingTreatmentDataBodyComparison {
    ...Slice

    primary {
      title: comparison_title {
        ...RichText
      }
      text: comparison_text {
        ...RichText
      }
      criteria1: comparison_first_item {
        ...RichText
      }
      criteria2: comparison_second_item {
        ...RichText
      }
      bgGrey: comparison_background_color
    }
    items {
      criteriaTitle: comparison_criteria {
        ...RichText
      }
      value1: comparison_criteria_first_value
      textValue1: comparison_criteria_first_value_text {
        ...RichText
      }
      value2: comparison_criteria_second_value
      textValue2: comparison_criteria_second_value_text {
        ...RichText
      }
    }
  }
`;
export default Comparison;
